import { useState, useEffect } from 'react';
import { X, Download, Bell, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

import { API } from '../config';

// Utility function to convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return; // Don't show for 7 days after dismissal
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay to not interrupt initial experience
      setTimeout(() => setShowInstallPrompt(true), 3000);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  if (isInstalled || !showInstallPrompt || !deferredPrompt) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-parchment border border-[#E5E0D0] rounded-xl shadow-lg p-4">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-charcoal-muted hover:text-charcoal transition-colors"
          aria-label="Dismiss"
          data-testid="dismiss-install-prompt"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-wine/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-wine" />
          </div>
          <div className="flex-1">
            <h3 className="font-playfair text-lg text-charcoal mb-1">
              Install Our App
            </h3>
            <p className="font-lora text-sm text-charcoal-muted mb-3">
              Add Letters of Unspoken Hearts to your home screen for the best reading experience.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleInstall}
                className="btn-primary text-sm px-4 py-2"
                data-testid="install-app-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Install
              </Button>
              <Button
                onClick={handleDismiss}
                variant="ghost"
                className="text-sm text-charcoal-muted"
              >
                Not now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PushNotificationPrompt({ token }) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 
                      'PushManager' in window && 
                      'Notification' in window;
    setIsSupported(supported);

    if (!supported) return;

    // Check existing subscription status
    checkSubscriptionStatus();

    // Check if prompt was dismissed
    const dismissed = localStorage.getItem('push-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 14) return; // Don't show for 14 days
    }

    // Show prompt after delay if not subscribed
    const timer = setTimeout(() => {
      if (!isSubscribed && Notification.permission !== 'denied') {
        setShowPrompt(true);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [token]);

  const checkSubscriptionStatus = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking push subscription:', error);
    }
  };

  const subscribeToPush = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setShowPrompt(false);
        return;
      }

      // Get VAPID public key from server
      const vapidResponse = await fetch(`${API}/push/vapid-key`);
      const { publicKey } = await vapidResponse.json();

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      const response = await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
          }
        })
      });

      if (response.ok) {
        setIsSubscribed(true);
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Error subscribing to push:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('push-prompt-dismissed', new Date().toISOString());
  };

  if (!isSupported || isSubscribed || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up"
      data-testid="push-notification-prompt"
    >
      <div className="bg-parchment border border-[#E5E0D0] rounded-xl shadow-lg p-4">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-charcoal-muted hover:text-charcoal transition-colors"
          aria-label="Dismiss"
          data-testid="dismiss-push-prompt"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-rose-muted/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Bell className="w-6 h-6 text-wine" />
          </div>
          <div className="flex-1">
            <h3 className="font-playfair text-lg text-charcoal mb-1">
              Stay Updated
            </h3>
            <p className="font-lora text-sm text-charcoal-muted mb-3">
              Get notified when new letter series are available.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={subscribeToPush}
                disabled={loading}
                className="btn-primary text-sm px-4 py-2"
                data-testid="enable-notifications-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                {loading ? 'Enabling...' : 'Enable'}
              </Button>
              <Button
                onClick={handleDismiss}
                variant="ghost"
                className="text-sm text-charcoal-muted"
              >
                Not now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotificationSettings({ token }) {
  const [pushEnabled, setPushEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSupported] = useState('serviceWorker' in navigator && 'PushManager' in window);

  useEffect(() => {
    if (isSupported) {
      checkPushStatus();
    }
  }, [token]);

  const checkPushStatus = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushEnabled(!!subscription);
    } catch (error) {
      console.error('Error checking push status:', error);
    }
  };

  const togglePushNotifications = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      if (pushEnabled) {
        // Unsubscribe
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
        
        await fetch(`${API}/push/unsubscribe`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        setPushEnabled(false);
      } else {
        // Subscribe
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const vapidResponse = await fetch(`${API}/push/vapid-key`);
        const { publicKey } = await vapidResponse.json();

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });

        await fetch(`${API}/push/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            keys: {
              p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
              auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
            }
          })
        });

        setPushEnabled(true);
      }
    } catch (error) {
      console.error('Error toggling push notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="text-charcoal-muted text-sm font-lora">
        Push notifications are not supported in this browser.
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-lora text-charcoal">Push Notifications</p>
        <p className="font-lora text-sm text-charcoal-muted">
          Get notified about new series and updates
        </p>
      </div>
      <button
        onClick={togglePushNotifications}
        disabled={loading}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          pushEnabled ? 'bg-wine' : 'bg-charcoal-light'
        }`}
        data-testid="push-toggle"
      >
        <span
          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            pushEnabled ? 'translate-x-6' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}
